/**
 * Social sign-in port — web implementation.
 *
 * Real browser OAuth for Google (Identity Services), Facebook (JS SDK) and
 * Apple (Sign in with Apple JS), mirroring the native googleAuth/facebookAuth/
 * appleAuth modules. Each provider returns a SocialAuthResult with the same
 * token shape the mobile flow sends to AuthContext.loginSSO:
 *   - Google:  idToken (GIS credential / ID token JWT)
 *   - Facebook: accessToken (FB access token)
 *   - Apple:    idToken (identity token JWT)
 *
 * Client IDs come from config/env (VITE_GOOGLE_CLIENT_ID, VITE_FACEBOOK_APP_ID,
 * VITE_APPLE_CLIENT_ID, VITE_APPLE_REDIRECT_URI). When a client ID is empty the
 * provider is treated as unavailable and its button is hidden by the UI.
 */
import {
  APPLE_CLIENT_ID,
  APPLE_REDIRECT_URI,
  FACEBOOK_APP_ID,
  GOOGLE_CLIENT_ID,
} from '../config/env';
import type { SocialAuthResult } from '../types';

// ─── Minimal window typings for the third-party SDKs ──────────────────────────

interface GoogleCredentialResponse {
  credential?: string;
  select_by?: string;
}

interface GooglePromptNotification {
  isDisplayMoment: () => boolean;
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  isNotDisplayed: () => boolean;
  getNotDisplayedReason: () => string;
  getSkippedReason: () => string;
  getDismissedReason: () => string;
}

export interface GoogleButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
  locale?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
  }) => void;
  prompt: (listener?: (n: GooglePromptNotification) => void) => void;
  renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void;
  disableAutoSelect: () => void;
  cancelOneTap: () => void;
}

interface GoogleNamespace {
  accounts?: { id?: GoogleAccountsId };
}

interface FacebookAuthResponse {
  accessToken: string;
  userID: string;
}

interface FacebookLoginResponse {
  status: 'connected' | 'not_authorized' | 'unknown';
  authResponse?: FacebookAuthResponse;
}

interface FacebookApiCallback {
  (response: unknown): void;
}

interface FacebookProfile {
  id?: string;
  name?: string;
  email?: string;
  picture?: { data?: { url?: string } };
}

interface FacebookNamespace {
  init: (config: {
    appId: string;
    cookie: boolean;
    xfbml: boolean;
    version: string;
  }) => void;
  login: (
    callback: (response: FacebookLoginResponse) => void,
    options: { scope: string; auth_type?: string },
  ) => void;
  api: (path: string, params: Record<string, unknown>, callback: FacebookApiCallback) => void;
  getLoginStatus: (callback: (response: FacebookLoginResponse) => void) => void;
  logout: (callback: (response: unknown) => void) => void;
}

interface AppleAuthorization {
  code: string;
  id_token: string;
  state?: string;
}

interface AppleSignInResponse {
  authorization?: AppleAuthorization;
  user?: {
    email?: string;
    name?: { firstName?: string; lastName?: string };
  };
}

interface AppleIDNamespace {
  auth: {
    init: (config: {
      clientId: string;
      scope: string;
      redirectURI: string;
      usePopup: boolean;
    }) => void;
    signIn: () => Promise<AppleSignInResponse>;
  };
}

declare global {
  interface Window {
    google?: GoogleNamespace;
    FB?: FacebookNamespace;
    fbAsyncInit?: () => void;
    AppleID?: AppleIDNamespace;
  }
}

// ─── Script loader ────────────────────────────────────────────────────────────

const loadScript = (id: string, src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Leaving the tag behind would make the early-return above treat the
      // next attempt as already loaded, so a transient failure would be
      // permanent.
      script.remove();
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });

/** base64url → UTF-8 string (for decoding JWT payloads). */
const decodeJwtPayload = (jwt: string): Record<string, unknown> => {
  try {
    const part = jwt.split('.')[1] ?? '';
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(
      part.length + ((4 - (part.length % 4)) % 4),
      '=',
    );
    // atob yields one char per byte; names outside Latin-1 are multi-byte
    // UTF-8 and would be mangled without decoding the bytes.
    const bytes = Uint8Array.from(atob(base64), char => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return {};
  }
};

// ─── Availability ─────────────────────────────────────────────────────────────

export const isGoogleAvailable = (): boolean => Boolean(GOOGLE_CLIENT_ID);
export const isFacebookAvailable = (): boolean => Boolean(FACEBOOK_APP_ID);
export const isAppleAuthSupported = (): boolean => Boolean(APPLE_CLIENT_ID);

// ─── Google ──────────────────────────────────────────────────────────────────

let googleLoading: Promise<void> | null = null;

const ensureGoogle = async (): Promise<GoogleAccountsId> => {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('google_not_configured');
  }
  if (!googleLoading) {
    // Drop the cached promise on failure, otherwise one offline moment
    // rejects every later attempt for the lifetime of the tab.
    googleLoading = loadScript('google-gsi', 'https://accounts.google.com/gsi/client').catch(
      error => {
        googleLoading = null;
        throw error;
      },
    );
  }
  await googleLoading;
  // The GIS script attaches `window.google` shortly after load; poll briefly
  // in case the load event fires before the global is assigned.
  let id = window.google?.accounts?.id;
  let tries = 0;
  while (!id && tries < 40) {
    await new Promise<void>(r => setTimeout(r, 50));
    id = window.google?.accounts?.id;
    tries += 1;
  }
  if (!id) throw new Error('google_sdk_unavailable');
  return id;
};

/** Maps a GIS credential (an ID token JWT) to the shared result shape. */
const googleResultFromCredential = (credential: string): SocialAuthResult => {
  const payload = decodeJwtPayload(credential);
  const email = typeof payload.email === 'string' ? payload.email : '';
  const name = typeof payload.name === 'string' ? payload.name : '';
  const picture = typeof payload.picture === 'string' ? payload.picture : '';
  const sub = typeof payload.sub === 'string' ? payload.sub : '';

  return {
    success: true,
    accessToken: credential,
    idToken: credential,
    user: { id: sub, name, email, photo: picture },
  };
};

/**
 * Renders Google's own sign-in button into `container` and reports the result.
 *
 * Web UI should prefer this over `signInWithGoogle`: One Tap only appears when
 * the browser already has a Google session, so a plain button wired to
 * `prompt()` silently does nothing for signed-out visitors. The rendered button
 * always opens the account chooser and yields the same ID token mobile sends.
 *
 * Returns a cleanup function that detaches the pending callback.
 */
export const mountGoogleButton = (
  container: HTMLElement,
  onResult: (result: SocialAuthResult) => void,
  options: GoogleButtonOptions = {},
): (() => void) => {
  let disposed = false;

  ensureGoogle()
    .then(id => {
      if (disposed) return;
      id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        auto_select: false,
        callback: response => {
          if (disposed) return;
          if (!response?.credential) {
            onResult({ success: false, error: 'no_credential' });
            return;
          }
          onResult(googleResultFromCredential(response.credential));
        },
      });
      // GIS appends rather than replaces, so clear any button from a prior
      // render (e.g. a re-render triggered by a width change).
      container.replaceChildren();
      id.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'center',
        // GIS rejects widths above 400px.
        width: Math.min(container.clientWidth || 400, 400),
        ...options,
      });
    })
    .catch((error: unknown) => {
      if (disposed) return;
      const message = error instanceof Error ? error.message : 'google_signin_failed';
      onResult({ success: false, error: message });
    });

  return () => {
    disposed = true;
  };
};

/**
 * One Tap sign-in. Kept for the cross-platform AuthContext contract; browser UI
 * should use `mountGoogleButton` instead (see the note above).
 */
export const signInWithGoogle = async (): Promise<SocialAuthResult> => {
  try {
    const id = await ensureGoogle();

    const credential = await new Promise<string>((resolve, reject) => {
      let settled = false;
      id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        auto_select: false,
        callback: response => {
          if (settled) return;
          if (response?.credential) {
            settled = true;
            resolve(response.credential);
          } else {
            settled = true;
            reject(new Error('no_credential'));
          }
        },
      });
      id.prompt(notification => {
        if (settled) return;
        if (
          notification.isNotDisplayed() ||
          notification.isSkippedMoment() ||
          notification.isDismissedMoment()
        ) {
          settled = true;
          reject(new Error('cancelled'));
        }
      });
    });

    return googleResultFromCredential(credential);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'google_signin_failed';
    if (message === 'cancelled') return { success: false, cancelled: true };
    return { success: false, error: message };
  }
};

export const logoutGoogle = (): void => {
  try {
    window.google?.accounts?.id?.disableAutoSelect();
  } catch {
    // ignore — no active session
  }
};

// ─── Facebook ─────────────────────────────────────────────────────────────────

let facebookLoading: Promise<void> | null = null;

const ensureFacebook = async (): Promise<FacebookNamespace> => {
  if (!FACEBOOK_APP_ID) {
    throw new Error('facebook_not_configured');
  }
  if (!facebookLoading) {
    facebookLoading = new Promise<void>(resolve => {
      // fbAsyncInit only fires once per SDK load. If the SDK is already there
      // (module re-evaluated by HMR, or another loader beat us to it) the hook
      // would never run and this promise would never settle.
      if (window.FB) {
        resolve();
        return;
      }
      // The SDK calls fbAsyncInit once it has loaded; init there, then resolve.
      window.fbAsyncInit = () => {
        window.FB?.init({
          appId: FACEBOOK_APP_ID,
          cookie: true,
          xfbml: true,
          version: 'v18.0',
        });
        resolve();
      };
      // Resolve on load failure too so the caller can surface a clean error
      // instead of hanging on a promise that never settles.
      loadScript('facebook-jssdk', 'https://connect.facebook.net/en_US/sdk.js').catch(
        () => resolve(),
      );
    });
  }
  await facebookLoading;
  if (!window.FB) throw new Error('facebook_sdk_unavailable');
  return window.FB;
};

export const signInWithFacebook = async (): Promise<SocialAuthResult> => {
  try {
    const FB = await ensureFacebook();

    const authResponse = await new Promise<FacebookAuthResponse | null>(
      (resolve) => {
        FB.login(
          response => {
            if (response.status === 'connected' && response.authResponse) {
              resolve(response.authResponse);
            } else {
              resolve(null);
            }
          },
          { scope: 'public_profile,email' },
        );
      },
    );

    if (!authResponse?.accessToken) {
      return { success: false, cancelled: true };
    }

    const profile = await new Promise<FacebookProfile>(resolve => {
      FB.api(
        '/me',
        { fields: 'id,name,email,picture.type(large)' },
        (response: unknown) => resolve((response ?? {}) as FacebookProfile),
      );
    });

    return {
      success: true,
      accessToken: authResponse.accessToken,
      user: {
        id: profile.id ?? authResponse.userID ?? '',
        name: profile.name ?? '',
        email: profile.email ?? '',
        photo: profile.picture?.data?.url ?? '',
      },
    };
  } catch (error) {
    // A user cancellation arrives as a non-connected login response above, not
    // as a throw, so anything landing here is a genuine failure.
    const message = error instanceof Error ? error.message : 'facebook_signin_failed';
    return { success: false, error: message };
  }
};

export const logoutFacebook = (): void => {
  try {
    // FB.init sets cookie: true, so the Facebook session outlives our own.
    // Without an explicit logout the next visitor on a shared browser is
    // silently signed back into the previous user's account.
    window.FB?.getLoginStatus(response => {
      if (response.status === 'connected') {
        window.FB?.logout(() => undefined);
      }
    });
  } catch {
    // ignore — SDK never loaded, so there is no session to clear
  }
};

// ─── Apple ────────────────────────────────────────────────────────────────────

let appleLoading: Promise<void> | null = null;

const ensureApple = async (): Promise<AppleIDNamespace> => {
  if (!APPLE_CLIENT_ID) {
    throw new Error('apple_not_configured');
  }
  if (!appleLoading) {
    appleLoading = loadScript(
      'apple-auth',
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
    ).catch(error => {
      appleLoading = null;
      throw error;
    });
  }
  await appleLoading;
  if (!window.AppleID) throw new Error('apple_sdk_unavailable');
  return window.AppleID;
};

export const signInWithApple = async (): Promise<SocialAuthResult> => {
  try {
    const AppleID = await ensureApple();
    AppleID.auth.init({
      clientId: APPLE_CLIENT_ID,
      scope: 'name email',
      redirectURI: APPLE_REDIRECT_URI || window.location.origin,
      usePopup: true,
    });

    const response = await AppleID.auth.signIn();
    const idToken = response.authorization?.id_token ?? '';
    if (!idToken) {
      return { success: false, error: 'no_identity_token' };
    }

    const payload = decodeJwtPayload(idToken);
    const email = typeof payload.email === 'string' ? payload.email : '';
    const sub = typeof payload.sub === 'string' ? payload.sub : '';
    const firstName = response.user?.name?.firstName ?? '';
    const lastName = response.user?.name?.lastName ?? '';
    const name = [firstName, lastName].filter(Boolean).join(' ').trim();

    return {
      success: true,
      accessToken: idToken,
      idToken,
      user: { id: sub, name, email, photo: '' },
    };
  } catch (error) {
    // Sign in with Apple JS rejects with a plain object, not an Error:
    // { error: 'popup_closed_by_user' }. Reading `.message` alone would
    // report every user cancellation as a hard failure.
    const appleCode =
      typeof (error as { error?: unknown })?.error === 'string'
        ? (error as { error: string }).error
        : '';
    const message =
      appleCode || (error instanceof Error ? error.message : 'apple_signin_failed');
    if (/cancel|popup_closed/i.test(message)) return { success: false, cancelled: true };
    return { success: false, error: message };
  }
};

export const logoutApple = (): void => {
  // Sign in with Apple JS has no client-side logout; the session is stateless.
};
